/* eslint-disable react/jsx-curly-newline */
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Logger } from '@saviynt/common';
import {
  AlertBannerProvider,
  Badge,
  Box,
  Button,
  ButtonSelect,
  Icon,
  InputField,
  Loader,
  StackingAlertBanner,
  Typography,
} from '@saviynt/design-system';
import cx from 'classnames';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';

import useActivationBanner from '../../hooks/use-activationbanner';
import useIgnoreBanner from '../../hooks/use-ignorebanner';
import { useInfiniteRules } from '../../hooks/use-infiniterules';
import { type RootState } from '../../services/types';
import { useStickyOffset } from '../../shared/hooks';
import { KpiTiles } from '../KpiTiles';

import { DashboardFilterPills } from './DashboardFilterPills';
import { useDashboardFilters, useSearchHandlers } from './hooks';
import { RuleCard } from './RuleCard';

import './RulesDashboard.css';

function RulesDashboard(): JSX.Element {
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [activeFilters, setActiveFilters] = useState({
    applications: [] as string[],
    entitlements: [] as string[],
  });
  const history = useHistory();

  const isMenuPinned = useSelector(
    (state: RootState) => state.user.isMenuPinned
  );

  // Check if any filters are active for badge display
  const hasActiveFilters = useMemo(
    () =>
      activeFilters.applications.length > 0 ||
      activeFilters.entitlements.length > 0,
    [activeFilters]
  );

  // Zero rules state tracking
  const [hasEverShownRules, setHasEverShownRules] = useState(false);

  // activation banner hook for rule activation banners
  const { successBanner, errorBanner } = useActivationBanner();

  // ignore banner hook for rule ignore banners
  const { ignoreBanner } = useIgnoreBanner();

  // Use search handlers hook
  // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
  const {
    searchInput,
    searchValue,
    handleSearchInputChange,
    handleSearchKeyDown,
    handleSearchClear,
    // eslint-disable-next-line @typescript-eslint/no-unsafe-call
  } = useSearchHandlers();

  // Height preservation for smooth loading transitions
  const cardsRef = useRef<HTMLDivElement>(null);
  const [preservedHeight, setPreservedHeight] = useState<number>();

  // Scroll tracking for infinite scroll
  const lastScrollTopRef = useRef(0);

  // Sticky header measurement for dynamic positioning
  const headerRef = useRef<HTMLElement>(null);

  // Measure dashboard header height for controls positioning under it
  useStickyOffset(headerRef.current, {
    cssVarName: '--rules-dashboard-header-height',
  });

  // Fetch filter options
  const { data: filterOptionsData, isLoading: isLoadingFilters } =
    useDashboardFilters();

  // Transform filter options for UI
  const availableFilters = useMemo(
    () => ({
      applications:
        filterOptionsData?.endpointNames.map((name) => ({
          label: name,
          value: name,
        })) ?? [],
      entitlements:
        filterOptionsData?.entitlementValueNames.map((name) => ({
          label: name, // TODO: Map to display names later
          value: name,
        })) ?? [],
    }),
    [filterOptionsData]
  );

  // API request body with filters (pagination handled by infinite query)
  const requestBody = useMemo(
    () => ({
      filters: {
        ...(searchValue && { search: searchValue }),
        ...(activeFilters.applications.length > 0 && {
          endpointName: activeFilters.applications,
        }),
        ...(activeFilters.entitlements.length > 0 && {
          entitlementValueName: activeFilters.entitlements,
        }),
      },
    }),
    [searchValue, activeFilters]
  );

  // Use infinite query for pagination support
  const {
    data: infiniteData,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
    error,
  } = useInfiniteRules(requestBody);

  // Get userAccess from the first page of data
  const userAccess = infiniteData?.pages[0]?.userAccess;

  // Get totalNewObjects from the first page of data
  const newRecommendationsCount = infiniteData?.pages[0]?.totalNewObjects ?? 0;

  // Flatten paginated data from all pages
  const allRules = useMemo(
    () => infiniteData?.pages.flatMap((page) => page.data) ?? [],
    [infiniteData]
  );

  // Track when rules are successfully loaded
  useEffect(() => {
    if (allRules.length > 0) {
      setHasEverShownRules(true);
    }
  }, [allRules.length]);

  // Zero state detection
  const isInitialZeroRules =
    !isLoading && !error && allRules.length === 0 && !hasEverShownRules;
  const isFilteredZeroRules =
    !isLoading && !error && allRules.length === 0 && hasEverShownRules;

  // Track cards container height for smooth loading transitions
  useEffect(() => {
    if (!isLoading && cardsRef.current) {
      setPreservedHeight(cardsRef.current.offsetHeight);
    }
  }, [isLoading, infiniteData]);

  // Window scroll handler for infinite scroll
  useEffect(() => {
    const handleWindowScroll = () => {
      // Don't trigger infinite scroll in zero state scenarios
      if (isInitialZeroRules || isFilteredZeroRules || !hasNextPage) {
        return;
      }

      const { scrollTop, scrollHeight, clientHeight } =
        document.documentElement;
      const isBottom = scrollHeight - scrollTop <= clientHeight + 20; // 20px threshold
      const isScrollingDown = scrollTop > lastScrollTopRef.current;

      if (isBottom && isScrollingDown && !isFetchingNextPage) {
        fetchNextPage().catch((fetchError) => {
          Logger.error('Failed to fetch next page', fetchError);
        });
      }

      lastScrollTopRef.current = scrollTop;
    };

    window.addEventListener('scroll', handleWindowScroll, { passive: true });

    return () => window.removeEventListener('scroll', handleWindowScroll);
  }, [
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
    isInitialZeroRules,
    isFilteredZeroRules,
  ]);

  // Filter handlers
  const handleSetFilter = useCallback(
    (category: 'applications' | 'entitlements', values: string[]) => {
      setActiveFilters((prev) => ({
        ...prev,
        [category]: values,
      }));
    },
    []
  );

  const handleClearAllFilters = useCallback(() => {
    setActiveFilters({
      applications: [],
      entitlements: [],
    });
  }, []);

  const handleViewDetails = (ruleId: number) => {
    history.push(`/rule-details/${ruleId}`);
  };

  // Main content renderer with zero state support
  const renderMainContent = () => {
    if (isLoading) {
      return (
        <Box className='Rules-Dashboard--isLoading'>
          <Loader kind='loop' format='inline' color='brand' size='large' />
        </Box>
      );
    }

    // Handle error state
    if (error) {
      Logger.error('Failed to load rules', error);

      return (
        <Box className='Rules-Dashboard Rules-Dashboard--error'>
          <Icon
            kind='AlertCriticalOutline'
            size='large'
            color='--color-background-critical-bold'
            className='Dashboard-zeroRulesIcon'
          />
          <Typography
            kind='body2-bold'
            color='--color-foreground-neutral-bold'
            className='Dashboard-zeroRulesMessage'>
            Error encountered.
          </Typography>
          <Typography kind='body2' color='--color-foreground-neutral-subtle'>
            Check logs and contact your administrator.
          </Typography>
        </Box>
      );
    }

    if (isInitialZeroRules) {
      return (
        <Box className='Dashboard-initialZeroRules'>
          <Icon
            kind='AlertInfo'
            size='large'
            color='--color-foreground-info-medium'
            className='Dashboard-zeroRulesIcon'
          />
          <Typography
            kind='body2-bold'
            color='--color-foreground-neutral-bold'
            className='Dashboard-zeroRulesMessage'>
            The feature for this data is not set up.
          </Typography>

          <Typography kind='body3' color='--color-foreground-neutral-subtle'>
            Try running the Rule Recommendations job to generate Rules
          </Typography>
          <Button className='Dashboard-runJobButton'>Run Job →</Button>
        </Box>
      );
    }

    if (isFilteredZeroRules) {
      return (
        <Box className='Dashboard-filteredZeroRules'>
          <Icon
            kind='AlertInfo'
            size='large'
            color='--color-foreground-info-medium'
            className='Dashboard-zeroRulesIcon'
          />
          <Typography
            kind='body2-bold'
            color='--color-foreground-neutral-bold'
            className='Dashboard-filteredZeroRulesMessage'>
            Sorry, there are no results for the search query you entered.
          </Typography>
        </Box>
      );
    }

    // Normal rules display with infinite scroll
    return (
      <>
        {allRules.map((rule) => (
          <RuleCard
            key={rule.recommendedObjectKey}
            rule={rule}
            onViewDetails={handleViewDetails}
            userAccess={userAccess}
            className='Rules-Dashboard-card'
          />
        ))}
        {isFetchingNextPage && (
          <Box className='Rules-Dashboard--isLoadingMore'>
            <Loader kind='loop' format='inline' color='brand' size='medium' />
          </Box>
        )}
      </>
    );
  };

  return (
    <>
      <Box className='Rules-Dashboard'>
        <header ref={headerRef} className='Rules-Dashboard-header'>
          <div className='Rules-Dashboard-header-iconTitleWrapper'>
            <Icon kind='Security' size='large' />
            <Typography kind='h1' className='Rules-Dashboard-title'>
              Rule Recommendations
            </Typography>
          </div>
          <Typography kind='body2' color='--color-foreground-neutral-subtle'>
            Top recommended rules from Intelligent Recommendations for quick
            conversion to Technical or Birthright Rule
          </Typography>
        </header>

        <KpiTiles />
        <Box tag='section' className='Rules-Dashboard-controlsFiltersWrapper'>
          <Box className='Rules-Dashboard-controls'>
            <Box className='Rules-Dashboard-search'>
              <InputField
                name='search'
                placeholder='Search all Recommendations'
                kind='search'
                prefixIcon={
                  <Icon
                    kind='Search'
                    color='--color-foreground-neutral-subtle'
                  />
                }
                value={searchInput}
                setValue={handleSearchInputChange}
                onKeyDown={handleSearchKeyDown}
                resetValue={handleSearchClear}
                size='medium'
              />
            </Box>
            <Box className='Rules-Dashboard-filters'>
              <ButtonSelect
                kind='rectangle'
                label='Filter'
                onClick={() => setIsFilterOpen(!isFilterOpen)}
                isOpen={isFilterOpen}
                prefixIcon={<Icon kind='Filter' />}
                BadgeComp={
                  hasActiveFilters ? (
                    <Badge kind='dot' color='primary' size='small' />
                  ) : null
                }
              />
              {/* for mvp2: <Button kind='outlined'>&lt;/&gt; View: Condition</Button> */}
            </Box>
          </Box>

          <DashboardFilterPills
            isVisible={isFilterOpen}
            activeFilters={activeFilters}
            availableFilters={availableFilters}
            isLoading={isLoadingFilters}
            onSetFilter={handleSetFilter}
            onClearAll={handleClearAllFilters}
          />
        </Box>

        <Box className='Rules-Dashboard-newRecommendationsCount'>
          {(() => {
            const isSearchingOrFiltering = searchValue || hasActiveFilters;
            // temporarily check both locations, later remove the fallback
            const totalRuleCount =
              infiniteData?.pages[0]?.totalCount ?? allRules[0]?.totalCount;

            return (
              Boolean(totalRuleCount) && (
                <Typography kind='h3' color='--color-border-neutral-bold'>
                  {isSearchingOrFiltering
                    ? `Total Results: ${totalRuleCount ?? 0}`
                    : Boolean(newRecommendationsCount) &&
                      `${newRecommendationsCount} New Rule Recommendations Found`}
                </Typography>
              )
            );
          })()}
        </Box>

        <div
          ref={cardsRef}
          style={{
            minHeight: isLoading ? preservedHeight : 'auto',
          }}>
          <Box
            tag='section'
            className='Rules-Dashboard-cards'
            role='list'
            aria-label='Rule recommendations'>
            {renderMainContent()}
          </Box>
        </div>
      </Box>

      <AlertBannerProvider
        className={cx(
          'Rules-Dashboard-alertBannerProvider',
          isMenuPinned && 'Rules-Dashboard-alertBannerProvider--isMenuPinned'
        )}>
        <StackingAlertBanner
          colorTheme='Success'
          title='Congratulations!'
          description={successBanner.message}
          isVisible={successBanner.isVisible}
          onCancel={successBanner.onClose}
          isColonVisible={false}
          shouldAutoDismiss
        />

        <StackingAlertBanner
          colorTheme='Critical'
          title='Oops!'
          description={errorBanner.message}
          isVisible={errorBanner.isVisible}
          onCancel={errorBanner.onClose}
          isColonVisible={false}
          shouldAutoDismiss={false}
        />

        <StackingAlertBanner
          colorTheme={ignoreBanner.colorTheme}
          title={ignoreBanner.colorTheme === 'Success' ? 'Success!' : 'Oops!'}
          description={ignoreBanner.message}
          isVisible={ignoreBanner.isVisible}
          onCancel={ignoreBanner.onClose}
          isColonVisible={false}
          shouldAutoDismiss={ignoreBanner.colorTheme === 'Success'}
        />
      </AlertBannerProvider>
    </>
  );
}

export default RulesDashboard;
